<script setup lang="ts">
/**
 * **画布适配层** —— 把 NpNode 的端口插槽替换成我们自己的端口件。
 *
 * 作用只有一个：把「视觉」和「谁接管连线」分开。
 *
 * 为什么用插槽而不是另写一个节点组件：
 *   NpNode 是视觉的唯一真源。如果适配层自己重画一遍行/render 逻辑，
 *   两边就会漂移 —— 调样式要改两处。
 *
 * 端口分两类，走两组不同的插槽：
 *   · socket-input / socket-output  → 真实端口，可拖拽连线
 *   · anchor-input / anchor-output  → 折叠分组的锚点，connectable=false，
 *                                     只用来挂住已有的线，防止从分组行拉出新线
 *
 * ⚠️ 2027：`<Handle>` 换成了我们自己的 `NpCanvasHandle`
 * （Vue Flow 已经删掉）。**插槽结构一个没变** —— 这正是当初走插槽的回报：
 * 换渲染层只动了这个文件里的 4 个模板块，NpNode 与视觉层零改动。
 *
 * 本文件属于应用层，不在 @node-print/designer 的公共 API 里。
 */
import NpCanvasHandle from './canvas/NpCanvasHandle.vue'
import NpNode from '../components/NpNode.vue'
import NpSocket from '../components/NpSocket.vue'
import type { GraphNodeModel, NodeDiagnostic } from '../types/node'
import type { WidgetValue } from '../types/widget'

const props = withDefaults(
  defineProps<{
    model: GraphNodeModel
    selected?: boolean
    /** 活动节点（属性面板跟随的那一个） */
    active?: boolean
    collapsed?: boolean
    linking?: boolean
    connectableSockets?: string[] | null
    /** 允许拖右边缘改宽 */
    resizable?: boolean
    /** 该节点的诊断，显示为 header 徽标 */
    diagnostics?: NodeDiagnostic[]
    /** 本次拖拽起点若在本节点上，这里是它的 handle id */
    originSocket?: string | null
  }>(),
  {
    selected: false,
    active: false,
    collapsed: false,
    linking: false,
    connectableSockets: null,
    resizable: false,
    diagnostics: () => [],
    originSocket: null,
  },
)

const emit = defineEmits<{
  (e: 'update:collapsed', value: boolean): void
  (e: 'group-toggle', groupId: string, collapsed: boolean): void
  (e: 'widget-update', rowId: string, widgetId: string, value: WidgetValue): void
  (e: 'resize', width: number): void
  (e: 'repeater-remove', rowId: string, index: number): void
  (e: 'toggle-mute', muted: boolean): void
  (e: 'toggle-hide-unused'): void
  (e: 'box-resize', width: number, height: number): void
  (e: 'note-update', text: string): void
  (e: 'row-toggle', rowId: string, value: boolean): void
  (e: 'rename', label: string): void
  (e: 'row-rename', rowId: string, index: number, name: string): void
  (e: 'repeater-add', rowId: string): void
  (e: 'enter-subgraph'): void
  (e: 'repeater-reorder', rowId: string, from: number, to: number): void
  (e: 'widget-resize', rowId: string, widgetId: string, height: number): void
}>()
</script>

<template>
  <NpNode
    :model="props.model"
    :selected="props.selected"
    :active="props.active"
    :collapsed="props.collapsed"
    :linking="props.linking"
    :connectable-sockets="props.connectableSockets"
    :resizable="props.resizable"
    :diagnostics="props.diagnostics"
    :origin-socket="props.originSocket"
    @update:collapsed="emit('update:collapsed', $event)"
    @group-toggle="
      (groupId, collapsed) => emit('group-toggle', groupId, collapsed)
    "
    @widget-update="
      (rowId, widgetId, value) => emit('widget-update', rowId, widgetId, value)
    "
    @resize="emit('resize', $event)"
    @toggle-mute="emit('toggle-mute', $event)"
    @toggle-hide-unused="emit('toggle-hide-unused')"
    @box-resize="(w, h) => emit('box-resize', w, h)"
    @note-update="emit('note-update', $event)"
    @row-toggle="(rowId, value) => emit('row-toggle', rowId, value)"
    @rename="emit('rename', $event)"
    @row-rename="(rowId, index, name) => emit('row-rename', rowId, index, name)"
    @repeater-add="(rowId) => emit('repeater-add', rowId)"
    @enter-subgraph="emit('enter-subgraph')"
    @repeater-reorder="
      (rowId, from, to) => emit('repeater-reorder', rowId, from, to)
    "
    @widget-resize="
      (rowId, widgetId, height) =>
        emit('widget-resize', rowId, widgetId, height)
    "
    @repeater-remove="
      (rowId, index) => emit('repeater-remove', rowId, index)
    "
  >
    <!-- ══ 真实端口：可拖拽连线 ══ -->
    <template #socket-input="{ def, state }">
      <NpCanvasHandle :node-id="model.id" :id="def.id" type="target" position="left">
        <!-- ⚠️ 端口的样子（类型 / 精确标记 / 句柄 id）由 `NpSocket` 落到 DOM 上 -->
        <NpSocket side="input" :def="def" v-bind="state" />
      </NpCanvasHandle>
    </template>

    <template #socket-output="{ def, state }">
      <NpCanvasHandle :node-id="model.id" :id="def.id" type="source" position="right">
        <NpSocket side="output" :def="def" v-bind="state" />
      </NpCanvasHandle>
    </template>

    <!--
      ══ 折叠分组的锚点 ══
      connectable=false → 不能从这里起线/落线（画布据此不起线，CSS 关掉事件）
      is-locked         → pointer-events:none + opacity（见 np-canvas.css）
      top 保持默认 50%   → 同组多条线汇聚到同一点，不做错开
    -->
    <template #anchor-input="{ sockets }">
      <NpCanvasHandle
        v-for="socket in sockets"
        :key="socket.id"
        :node-id="model.id"
        :id="socket.id"
        type="target"
        position="left"
        :connectable="false"
      />
    </template>

    <template #anchor-output="{ sockets }">
      <NpCanvasHandle
        v-for="socket in sockets"
        :key="socket.id"
        :node-id="model.id"
        :id="socket.id"
        type="source"
        position="right"
        :connectable="false"
      />
    </template>

    <template #widget-custom="slotProps">
      <slot name="widget-custom" v-bind="slotProps" />
    </template>

    <template #row-display="slotProps">
      <slot name="row-display" v-bind="slotProps" />
    </template>
  </NpNode>
</template>
